/**
 * Switch Cost Table Component
 *
 * Displays a detailed breakdown of costs associated with switching funds,
 * including exit load, STT, stamp duty, STCG, LTCG, and total cost.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatINR } from "@/lib/utils";
import { Receipt, AlertCircle, Info } from "lucide-react";

// ============================================================================
// Types
// ============================================================================

/**
 * Switch cost data structure
 */
export interface SwitchCostData {
  /** Exit load amount */
  exitLoad: number;
  /** Securities Transaction Tax */
  stt: number;
  /** Stamp duty on purchase */
  stampDuty: number;
  /** Short-term capital gains tax */
  stcgTax?: number;
  /** Long-term capital gains tax */
  ltcgTax?: number;
  /** Total cost of switching */
  totalCost: number;
  /** Cost as percentage of redemption value */
  costPercentage: number;
  /** Additional notes about costs */
  notes?: string[];
}

/**
 * Props for the SwitchCostTable component
 */
export interface SwitchCostTableProps {
  /** Switch cost data */
  data: SwitchCostData;
  /** Redemption value for context */
  redemptionValue?: number;
  /** Optional title override */
  title?: string;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Helper Components
// ============================================================================

/**
 * Individual cost row in the table
 */
function CostRow({
  label,
  amount,
  description,
  isTotal = false,
  isHighlighted = false,
}: {
  label: string;
  amount: number;
  description?: string;
  isTotal?: boolean;
  isHighlighted?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between py-2",
        isTotal && "border-t border-border pt-3 mt-2",
        isHighlighted && "bg-amber-50 dark:bg-amber-900/20 -mx-3 px-3 rounded-md"
      )}
    >
      <div className="flex-1">
        <span
          className={cn(
            "text-sm",
            isTotal ? "font-semibold" : "text-muted-foreground"
          )}
        >
          {label}
        </span>
        {description && (
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>
      <span
        className={cn(
          "font-mono text-sm",
          isTotal && "font-semibold text-base",
          amount > 0 && !isTotal && "text-amber-600 dark:text-amber-400"
        )}
      >
        {formatINR(amount)}
      </span>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Switch Cost Table
 *
 * Displays a detailed breakdown of all costs involved in switching
 * from one fund to another, including regulatory fees and taxes.
 *
 * @example
 * ```tsx
 * <SwitchCostTable
 *   data={{
 *     exitLoad: 500,
 *     stt: 40,
 *     stampDuty: 60,
 *     stcgTax: 1200,
 *     ltcgTax: 3500,
 *     totalCost: 5300,
 *     costPercentage: 1.32,
 *     notes: ["Exit load applies to recent purchases"]
 *   }}
 *   redemptionValue={400000}
 * />
 * ```
 */
export function SwitchCostTable({
  data,
  redemptionValue,
  title = "Switch Cost Breakdown",
  className,
}: SwitchCostTableProps) {
  const hasTaxes = (data.stcgTax && data.stcgTax > 0) || (data.ltcgTax && data.ltcgTax > 0);

  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <Receipt className="h-4 w-4 text-muted-foreground" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        {/* Redemption Value Context */}
        {redemptionValue && (
          <div className="flex items-center justify-between py-2 pb-3 border-b border-border/50 mb-2">
            <span className="text-sm text-muted-foreground">
              Redemption Value
            </span>
            <span className="font-mono text-sm font-medium">
              {formatINR(redemptionValue)}
            </span>
          </div>
        )}

        {/* Exit Load */}
        <CostRow
          label="Exit Load"
          amount={data.exitLoad}
          description={data.exitLoad > 0 ? "Charged on early redemption" : "No exit load applicable"}
          isHighlighted={data.exitLoad > 0}
        />

        {/* STT */}
        <CostRow
          label="Securities Transaction Tax (STT)"
          amount={data.stt}
          description="0.001% on redemption value"
        />

        {/* Stamp Duty */}
        <CostRow
          label="Stamp Duty"
          amount={data.stampDuty}
          description="0.015% on new investment"
        />

        {/* Tax Section */}
        {hasTaxes && (
          <>
            <div className="border-t border-border/50 my-3" />
            <div className="flex items-center gap-2 mb-2">
              <Info className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Estimated Tax Liability
              </span>
            </div>

            {data.stcgTax !== undefined && data.stcgTax > 0 && (
              <CostRow
                label="Short-Term Capital Gains Tax"
                amount={data.stcgTax}
                description="20% on gains from units held < 12 months"
              />
            )}

            {data.ltcgTax !== undefined && data.ltcgTax > 0 && (
              <CostRow
                label="Long-Term Capital Gains Tax"
                amount={data.ltcgTax}
                description="12.5% on gains above Rs 1.25L from units held > 12 months"
              />
            )}
          </>
        )}

        {/* Total Cost */}
        <CostRow label="Total Cost" amount={data.totalCost} isTotal />

        {/* Cost Percentage */}
        <div className="flex items-center justify-between py-2 text-xs text-muted-foreground">
          <span>Cost as % of redemption</span>
          <span className="font-mono">
            {data.costPercentage.toFixed(2)}%
          </span>
        </div>

        {/* Notes */}
        {data.notes && data.notes.length > 0 && (
          <div className="mt-4 pt-3 border-t border-border/50">
            <div className="flex items-start gap-2 text-xs text-muted-foreground">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <ul className="space-y-1">
                {data.notes.map((note, index) => (
                  <li key={index}>{note}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Skeleton Loader
// ============================================================================

/**
 * Skeleton loader for SwitchCostTable
 */
export function SwitchCostTableSkeleton({
  className,
}: {
  className?: string;
}) {
  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-5 w-40" />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Cost rows */}
        {[...Array(5)].map((_, index) => (
          <div key={index} className="flex items-center justify-between py-2">
            <div className="space-y-1">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-48" />
            </div>
            <Skeleton className="h-4 w-16" />
          </div>
        ))}

        {/* Total row */}
        <div className="border-t pt-3 mt-2">
          <div className="flex items-center justify-between">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-5 w-20" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default SwitchCostTable;
